// LinkWorker.jsx
import BuildersLogo from "@components/BuildersLogo";
import HeadlessButton from "@components/Button";
import LanguageToggle from "@components/constant_components/LanguageToggle";
import FormikInput from "@components/form/FormikInput";
import Spinner from "@components/Spinner";
import axiosClient from "@service/axiosClient";
import { Field, Form, Formik } from "formik";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@components/context/AuthContext";
import * as Yup from "yup";

export default function LinkWorker() {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleInfo } = useAuth();
  const formikRef = useRef(null);

  useEffect(() => {
    formikRef.current?.validateForm();
  }, [t]);

  const initialValues = { temp_code: "" };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        temp_code: Yup.string().required(t("register.validation.tempCode")),
      }),
    [t]
  );

  async function onSubmit(values, { setSubmitting, setErrors }) {
    try {
      // 1️ Pull only the email out of what we stored
      const stored = JSON.parse(sessionStorage.getItem("pending_user_data"));
      if (!stored || !stored.email) {
        setErrors({ temp_code: t("register.errors.missingRegistration") });
        return;
      }

      // 2️ Call the finalize‐registration API
      await axiosClient.post("/users/finalize_registration", {
        email: stored.email,
        contractor_id: contractorId,
        temp_code: values.temp_code,
      });
      if (stored.origin === "login_page") {
        const response = await axiosClient.get("/auth/info/admin");
        let data = {
          role: "user",
          plants: [],
          email: values.email,
        };
        data = {
          userType: response.data.userType,
          plants: response.data.plants,
          roles: response.data.roles,
          permissions: response.data.permissions,
          user_id:
            response.data.userType === "user"
              ? response.data.user_id
              : response.data.admin_id,
          user_name:
            response.data.userType === "user"
              ? response.data.user_name
              : response.data.admin_name,
          worker_id: response.data.worker_id,
        };
        handleInfo(data);
        sessionStorage.removeItem("pending_user_data");
        navigate(`/contractor/${response.data.user_id}`);
        return;
      }
      // 3️ Clean up + redirect
      sessionStorage.removeItem("pending_user_data");
      navigate("/login/user");
    } catch (err) {
      console.error("Finalize failed:", err);
      setErrors({
        temp_code:
          err?.response?.data?.message || t("register.errors.invalidCode"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="tw-form-container"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      <div className="tw-form-language-toggle">
        <LanguageToggle />
      </div>
      <div className="tw-form-overlay" />
      <div className="tw-form-wrapper">
        <div className="tw-flex tw-justify-center tw-mb-4">
          <BuildersLogo />
        </div>

        <h2 className="tw-form-heading">{t("register.enterTempCode")}</h2>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="tw-space-y-4">
              <Field
                name="temp_code"
                label={t("register.tempCode")}
                as={FormikInput}
                error={touched.temp_code && errors.temp_code}
              />

              <HeadlessButton
                type="submit"
                disabled={isSubmitting}
                className="tw-form-button"
              >
                {isSubmitting ? (
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    {t("register.processing")}
                    <Spinner />
                  </div>
                ) : (
                  t("register.confirm")
                )}
              </HeadlessButton>

              <a
                href="/logout"
                className="tw-text-sm tw-text-blue-500 tw-block tw-text-center"
              >
                {t("register.back")}
              </a>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
